import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';

const PhoneInput = ({ value, onChange, ...rest }) => (
  <input
    type="tel" inputMode="numeric" pattern="[0-9]{10}" maxLength={10}
    value={value}
    onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 10))}
    {...rest}
  />
);

const Register = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: '', password: '', name: '', phone: '', role: 'customer' });
  const [agree, setAgree] = useState(false);
  const [showTnc, setShowTnc] = useState(false);
  const [busy, setBusy] = useState(false);

  const upd = k => e => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { toast.error('Please accept the PrintingKit Agreement'); return; }
    if (!/^[6-9]\d{9}$/.test(form.phone)) {
      toast.error('Enter a valid 10-digit Indian mobile number (starts with 6-9)');
      return;
    }
    setBusy(true);
    try {
      await register(form);
      toast.success('Account created');
      nav('/', { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registration failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6">
      <div className="reg-mark">
        <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: 'var(--pk-primary)' }}>— New here?</div>
        <h1 className="font-display text-[36px] mt-2 leading-tight">Create account.</h1>
        <p className="text-[12px] text-neutral-600 mt-2">Order prints in a tap. Sign up as a customer.</p>
      </div>

      <form onSubmit={submit} className="mt-5 space-y-3">
        <div><label className="pk-label">Name</label><input required className="pk-input" value={form.name} onChange={upd('name')} data-testid="register-name" /></div>
        <div><label className="pk-label">Email</label><input required type="email" className="pk-input" value={form.email} onChange={upd('email')} data-testid="register-email" /></div>
        <div>
          <label className="pk-label">Phone (10 digits)</label>
          <PhoneInput
            required
            className="pk-input font-mono tracking-widest"
            value={form.phone}
            onChange={(v) => setForm({ ...form, phone: v })}
            placeholder="9876543210"
            data-testid="register-phone"
          />
          <div className={`text-[10px] mt-1 tracking-wider ${form.phone.length === 10 && /^[6-9]/.test(form.phone) ? 'text-emerald-600' : 'text-neutral-400'}`}>
            {form.phone.length}/10 digits{form.phone.length === 10 && !/^[6-9]/.test(form.phone) ? ' — must start with 6-9' : ''}
          </div>
        </div>
        <div><label className="pk-label">Password</label><input required type="password" className="pk-input" value={form.password} onChange={upd('password')} data-testid="register-password" /></div>

        <label className="flex items-start gap-2 mt-3">
          <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} className="mt-1" data-testid="register-agree" />
          <div className="text-[12px] leading-relaxed">
            I accept the <button type="button" onClick={() => setShowTnc(true)} className="underline font-bold" data-testid="open-tnc">PrintingKit Agreement</button>, <Link to="/terms" target="_blank" className="underline font-bold" data-testid="register-terms-link">Terms</Link> and <Link to="/privacy" target="_blank" className="underline font-bold" data-testid="register-privacy-link">Privacy Policy</Link>.
          </div>
        </label>

        <button type="submit" disabled={busy} className="pk-btn w-full mt-4" data-testid="register-submit">
          {busy ? 'Creating…' : 'Create account'}
        </button>
      </form>

      <div className="mt-4 text-[12px] text-neutral-600">Already have an account? <Link to="/login" className="underline font-bold">Login</Link></div>

      <div className="mt-8 pt-6 border-t border-dashed border-neutral-300 text-center">
        <div className="text-[9px] tracking-[0.22em] uppercase text-neutral-400">Own a print shop?</div>
        <Link to="/partner-signup" className="text-[11px] text-neutral-500 underline mt-1 inline-block hover:text-neutral-700" data-testid="partner-signup-link">
          Apply to join our network →
        </Link>
      </div>

      {showTnc && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowTnc(false)}>
          <div className="bg-white pk-border-thick brutal-shadow-lg max-w-md max-h-[80vh] overflow-y-auto p-6 slide-up" onClick={e => e.stopPropagation()}>
            <div className="font-display text-[22px] mb-2">PrintingKit Agreement</div>
            <div className="text-[12px] leading-relaxed text-neutral-700 space-y-3">
              <p><b>1. Coordination platform.</b> PrintingKit connects customers with independent print partners. Zero brokerage from either side.</p>
              <p><b>2. Payments.</b> The partner receives the full amount paid for the print job.</p>
              <p><b>3. Color accuracy.</b> Digital-to-print CMYK conversion carries a 2–8% color variation.</p>
              <p><b>4. Delivery.</b> Depending on distance and city, orders may be delivered via Rapido, Porter, or self-pickup.</p>
              <p><b>5. Refunds.</b> Refunds are granted only when the printing itself is defective. Errors in customer-uploaded artwork are non-refundable.</p>
              <p><b>6. Turnaround.</b> Most orders are printed and dispatched within 24 hours of partner acceptance.</p>
              <p><b>7. Content.</b> Uploaded artwork must not infringe third-party rights.</p>
            </div>
            <button type="button" onClick={() => setShowTnc(false)} className="pk-btn w-full mt-4" data-testid="close-tnc">I understand</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
